import React, { useRef, useState } from "react";
import useAuthStore from "@/store/useAuthStore";
import { Eye } from "lucide-react";
import {
  Switch,
  FormControlLabel,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Button,
} from "@mui/material";
import OrganizationDetailsDialog from "../OrganizationDetails";
import { useTenantById } from "@/hooks/useTenantDashboardService";
import { formatDateUS } from "@/utils/dateUtils";
import Pagination from "../Pagination";
import { useDeleteTenant } from "@/hooks/useTenantDashboardService";
import { Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface LicenseData {
  id?: string;
  contactEmail: string;
  tenant: string;
  assigned: number;
  used: number;
  expiry: string;
  type: string;
  hasStripePlan?: boolean;
  createdAt?: string;
  accountType?: string;
  token: number;
  status?: boolean | string;
}

interface LicenseTableProps {
  data: LicenseData[];
  showHeading?: boolean;
  onScrollEnd?: () => void;
  isFetching?: boolean;
  totalPages: number;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  setShouldFetch?: () => void;
  searchQuery?: string;
  setSearchQuery?: (q: string) => void;
}

const getInitials = (name: string) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  return parts.length > 1
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
};

const TenantLicenseTable: React.FC<LicenseTableProps> = ({
  data,
  showHeading = true,
  totalPages,
  currentPage,
  isFetching,
  setCurrentPage,
  setShouldFetch,
  searchQuery,
  setSearchQuery,
}) => {
  const { role } = useAuthStore();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [tenantIdToDelete, setTenantIdToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { mutate: deleteTenant } = useDeleteTenant();
  const navigate = useNavigate();
  console.log(data, "data");
  const { data: selectedTenantData } = useTenantById({
    tenantId: selectedTenantId ?? "",
    query: { include: "tenantProfile,usage_log,planMaster,subscriptions" },
  });

  const [selectedRowData, setSelectedRowData] = useState<LicenseData | null>(null);

  const handleViewClick = (id: string, row: LicenseData) => {
    setSelectedTenantId(id);
    setSelectedRowData(row);
    setDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!tenantIdToDelete) return;

    setIsDeleting(true);

    deleteTenant(tenantIdToDelete, {
      onSuccess: () => {
        setIsDeleting(false);
        setShowDeleteDialog(false);
        setTenantIdToDelete(null);
        setShouldFetch?.();
      },
      onError: (error) => {
        console.error("Delete Tenant Error:", error);
        setIsDeleting(false);
      },
    });
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setTenantIdToDelete(null);
  };

  const handleDelete = (id: string) => {
    setTenantIdToDelete(id);
    setShowDeleteDialog(true);
  };

  return (
    <div
      className="bg-[#1C1A26] p-4 rounded-lg overflow-x-auto overflow-y-auto"
      ref={scrollRef}
    >
      {showHeading && (
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold text-white">
            {role === "ADMIN" ? "Users" : "Recent Organizations"}
          </h2>
          <div className="flex items-center gap-4">
            {setSearchQuery && (
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery || ""}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[#29273A] text-white px-4 py-2 rounded-lg border border-[#313131] focus:outline-none focus:border-[#B8F890]"
              />
            )}
            {role !== "MANAGER" && (
              <button
                className="px-4 py-2 bg-[#B8F890] font-semibold text-sm text-black rounded-full cursor-pointer whitespace-nowrap"
                onClick={() => {
                  navigate("/add/company");
                }}
              >
                Add New Organization
              </button>
            )}
          </div>
        </div>
      )}

      <table className="w-full text-sm text-left border-collapse">
        <thead>
          <tr className="bg-[#29273A] text-gray-200">
            <th className="py-3 px-4 rounded-tl-lg">Organization Name</th>
            <th className="py-3 px-4">Email</th>
            <th className="py-3 px-4 text-center">Account Type</th>
            <th className="py-3 px-4 text-center">Created At</th>
            <th className="py-3 px-4">Expiry Date</th>
            <th className="py-3 px-4 text-center">Status</th>
            <th className="py-3 px-4 text-center rounded-tr-lg">View</th>
            <th className="py-3 px-4 text-center">Delete</th>
          </tr>
        </thead>
        <tbody>
          {isFetching ? (
            <tr>
              <td colSpan={9} className="py-6 text-center text-gray-400">
                <div className="flex justify-center items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Loading data...</span>
                </div>
              </td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr
                key={idx}
                className="border-t border-[#313131] text-white hover:bg-[#2b2939] transition-colors"
              >
                <td className="py-2.5 px-4 max-w-[180px]">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-semibold text-xs"
                      style={{
                        backgroundColor: "color-mix(in srgb, var(--color-stat-accent) 15%, transparent)",
                        color: "var(--color-stat-accent)",
                      }}
                    >
                      {getInitials(row.tenant)}
                    </div>
                    <Tooltip title={row.tenant} arrow placement="top">
                      <span className="truncate font-medium">{row.tenant}</span>
                    </Tooltip>
                  </div>
                </td>
                <td className="py-2.5 px-4 max-w-[180px] overflow-hidden whitespace-nowrap text-ellipsis">
                  <Tooltip title={row.contactEmail} arrow placement="top">
                    <span className="inline-block truncate max-w-full text-left">
                      {row.contactEmail}
                    </span>
                  </Tooltip>
                </td>
                <td className="py-2.5 px-4 text-center">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: "color-mix(in srgb, var(--color-stat-accent) 15%, transparent)", color: "var(--color-stat-accent)" }}>
                    {row.accountType ? row.accountType.charAt(0).toUpperCase() + row.accountType.slice(1).toLowerCase() : "Company"}
                  </span>
                </td>
                <td className="py-2.5 px-4 text-center">
                  {row.createdAt ? formatDateUS(row.createdAt) : "N/A"}
                </td>
                <td className="py-2.5 px-4">
                  {row.expiry ? (
                    <span className="font-medium text-white">{formatDateUS(row.expiry)}</span>
                  ) : row.hasStripePlan ? (
                    <span className="text-gray-300">Active</span>
                  ) : (
                    <span className="text-gray-400">No Plan</span>
                  )}
                </td>
                <td className="py-2.5 px-4 text-center">
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={!!row.status}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "var(--color-stat-accent)",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: "var(--color-stat-accent)",
                          },
                        }}
                      />
                    }
                    label=""
                    sx={{ margin: 0 }}
                  />
                </td>
                <td className="py-2.5 px-4 text-center">
                  <button
                    className="hover:text-blue-400 transition-colors"
                    aria-label="View Details"
                    onClick={() => handleViewClick(row.id ?? "", row)}
                  >
                    <Eye size={18} />
                  </button>
                </td>
                <td className="py-2.5 px-4 text-center">
                  <button
                    className="hover:text-red-400 transition-colors"
                    aria-label="Delete Tenant"
                    onClick={() => handleDelete(row.id ?? "")}
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div className="mt-4 flex justify-center">
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage ?? 1}
          setCurrentPage={setCurrentPage ?? (() => {})}
        />
      </div>
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        PaperProps={{ sx: { backgroundColor: "#1E1D2A", color: "white" } }}
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#B8F890" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to delete this tenant?
          </Typography>
          <Box display="flex" justifyContent="flex-end" gap={2}>
            {isDeleting ? (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                <span>Deleting...</span>
              </div>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={handleCancelDelete}
                  sx={{
                    color: "#B8F890",
                    borderColor: "#B8F890",
                    "&:hover": { backgroundColor: "#2B2A3A" },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmDelete}
                  sx={{
                    backgroundColor: "#B8F890",
                    color: "black",
                    fontWeight: "bold",
                    "&:hover": { backgroundColor: "#D7FF9B" },
                  }}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {dialogOpen && selectedTenantData && (
        <OrganizationDetailsDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          data={{
            ...selectedTenantData,
            expiryDate:
              (selectedTenantData as any)?.subscriptions?.find((s: any) => s.status === "ACTIVE")?.endDate ||
              (selectedTenantData as any)?.subscriptions?.[0]?.endDate ||
              selectedRowData?.expiry,
            planMaster: selectedTenantData.planMaster
              ? {
                  ...selectedTenantData.planMaster,
                  planMasterId:
                    selectedTenantData.planMaster.planMasterId ??
                    selectedTenantData.planMaster.id,
                }
              : undefined,
            tenantProfile: selectedTenantData.tenantProfile
              ? {
                  ...selectedTenantData.tenantProfile,
                  tenantId:
                    selectedTenantData.tenantProfile.tenantId ??
                    selectedTenantData.id,
                  about: selectedTenantData.tenantProfile.about ?? "",
                  competitors:
                    selectedTenantData.tenantProfile.competitors ?? [],
                  partners: selectedTenantData.tenantProfile.partners ?? [],
                  productsServices:
                    selectedTenantData.tenantProfile.productsServices ?? [],
                  salesMethodology:
                    selectedTenantData.tenantProfile.salesMethodology ?? [],
                  targetIndustries:
                    selectedTenantData.tenantProfile.targetIndustries ?? [],
                  uniqueSellingPoints:
                    selectedTenantData.tenantProfile.uniqueSellingPoints ?? [],
                  valueProposition:
                    selectedTenantData.tenantProfile.valueProposition ?? [],
                  updatedby: selectedTenantData.tenantProfile.updatedby ?? null,
                }
              : undefined,
          }}
        />
      )}
    </div>
  );
};

export default TenantLicenseTable;
